import { Injectable } from '@angular/core';
import { BaseService, IPaginatorService } from './base';
import { Observable, of, from } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { BasePaginator } from '../model/base/base-model';
import {
    DependencyMap,
    DependencyMapCatalogMetadataBackfillResult,
    DependencyMapCatalogRequest,
    DependencyMapCatalogResponse,
    DependencyMapCredentialAssignment,
    DependencyMapCredentialBackfillResult,
    DependencyMapExecutionMetadataReport
} from '../model/dependency-map';
import { CatalogFiltersFeatures } from '@vex/model/available-catalogs';
import { finalize, share, tap } from 'rxjs/operators';
import { SyncService } from '@vex/http-service/sync.service';
import { EnvironmentType } from '@vex/model/dependency-map';
@Injectable({
    providedIn: 'root'
})
export class DependencyMapService extends IPaginatorService {
    readonly baseEndpoint = 'DependencyMap';

    constructor(private http: HttpClient, private syncService: SyncService) {
        super();
    }

    getPaginatedData<DependencyMap>(
        sort: string,
        order: string,
        page: number,
        size: number,
        filter: string
    ): Observable<BasePaginator<DependencyMap>> {
        let url = this.baseUrl + this.baseEndpoint + '/GetPaginated/' + page + '/' + size + '?';

        if (sort && order) {
            url += `sort=${sort}&order=${order}`;
        }
        if (filter) {
            url += (sort ? '&' : '') + `filter=${filter}`;
        }

        return this.http.get<BasePaginator<DependencyMap>>(url);
    }

    getDependenciesMaps() {
        return this.http.get(this.baseUrl + this.baseEndpoint);
    }

    getCatalogPage(request: DependencyMapCatalogRequest): Observable<DependencyMapCatalogResponse> {
        return this.http.post<DependencyMapCatalogResponse>(
            this.baseUrl + this.baseEndpoint + '/GetCatalogPage',
            request
        );
    }

    getExecutionMetadataIssues(batchSize: number = 500): Observable<DependencyMapExecutionMetadataReport> {
        return this.http.post<DependencyMapExecutionMetadataReport>(
            `${this.baseUrl}${this.baseEndpoint}/ExecutionMetadata/DryRun?batchSize=${batchSize}`,
            {}
        );
    }

    backfillCredentialReferenceTypes(
        assignments: DependencyMapCredentialAssignment[]
    ): Observable<DependencyMapCredentialBackfillResult> {
        return this.http.post<DependencyMapCredentialBackfillResult>(
            this.baseUrl + this.baseEndpoint + '/CredentialReferenceType/Backfill',
            { DryRun: false, Assignments: assignments }
        );
    }

    backfillCatalogMetadata(batchSize: number = 500): Observable<DependencyMapCatalogMetadataBackfillResult> {
        return this.http.post<DependencyMapCatalogMetadataBackfillResult>(
            this.baseUrl + this.baseEndpoint + '/CatalogMetadata/Backfill',
            { BatchSize: batchSize }
        );
    }

    getAllDependencies(): Observable<any> {
        return from(
            this.syncService.checkAndSyncData('DependencyMap','DependencyMap').then(async () => {
                let data = await this.syncService.sync('DependencyMap');
                return data;
            })
        )
    }

    getDependencyMapsForSelect(): Observable<any[]> {
        let promise = (async ()=>{
            
            await this.syncService.checkAndSyncData('DependencyMap', 'DependencyMap');
            
            let dependencyMaps = await this.syncService.sync('DependencyMap');
            let resources: any[] = [];
            for(let i = 0; i < dependencyMaps.length; i++) {
                let dependencyMap = dependencyMaps[i];
                resources.push({
                    Id: dependencyMap.Id,
                    Name: dependencyMap.Name,
                    Description: dependencyMap.Description,
                    EnvironmentType: dependencyMap.EnvironmentType,
                    ExecutionEngine: dependencyMap.ExecutionEngine,
                    CredentialReferenceType: dependencyMap.CredentialReferenceType,
                    ProviderCollectionEntryId: dependencyMap.ProviderCollectionEntryId,
                    ShowDeviceSelection: dependencyMap.ShowDeviceSelection === true,
                    AllowMultipleDeviceSelection: dependencyMap.AllowMultipleDeviceSelection === true,
                    AllowedDependencyMapIds: dependencyMap.AllowedDependencyMapIds || [],
                    IntegrateWithIpam: dependencyMap.IntegrateWithIpam,
                    Image: dependencyMap.Image,
                    RemediationProvider: dependencyMap.RemediationProvider
                })
            }
            return resources;
        })();
        return from(promise);
    }

    getForSelectPaginated(param): Observable<any> {
        const request = {
            ...(param || {}),
            Providers: param?.Providers ?? param?.Clouds ?? [],
            EnvironmentType: param?.AllEnvironmentTypes === true
                ? null
                : (param?.EnvironmentType ?? EnvironmentType.DataCenter)
        };
        return this.http.post<any>(this.baseUrl + this.baseEndpoint + '/GetForSelectPaginatedPost', request);
    }

    getResourcesByDependencyMapPaginated(filter: {
        DependencyMapId: string;
        Filter?: string;
        PageNumber?: number;
        PageSize?: number;
    }): Observable<any> {
        return this.http.post<any>(
            this.baseUrl + this.baseEndpoint + '/GetResourcesByDependencyMapPaginated',
            filter
        );
    }

    getDependencyMapsCollectionEntriesForSelect(): Observable<any> {
        let promise = (async ()=>{
            
            await this.syncService.checkAndSyncData('DependencyMap', 'DependencyMap');
            await this.syncService.checkAndSyncData('CollectionEntry', 'CollectionEntry');
            
            let dependencyMaps = await this.syncService.sync('DependencyMap');
            let allCollectionEntries = await this.syncService.sync('CollectionEntry');
            let resources: any[] = [];
            var collectionEntries = allCollectionEntries.filter(m => m.CollectionId == "5e92f6be4dec051cac3465d0"); // Resources
            for(let i = 0; i < dependencyMaps.length; i++) {
                let dependencyMap = dependencyMaps[i];
                let collectionEntry = collectionEntries.find(collectionEntry => 
                        collectionEntry.CollectionAttributeValues.find(collectionAttributeValue => collectionAttributeValue.Name == "Name")?.Value?.toString().toLowerCase().trim() == dependencyMap.Name?.toString().toLowerCase().trim()
                    );
                resources.push({
                    Id: dependencyMap.Id,
                    Name: dependencyMap.Name,
                    Description: dependencyMap.Description,
                    EnvironmentType: dependencyMap.EnvironmentType,
                    ExecutionEngine: dependencyMap.ExecutionEngine,
                    CredentialReferenceType: dependencyMap.CredentialReferenceType,
                    ProviderCollectionEntryId: dependencyMap.ProviderCollectionEntryId,
                    ShowDeviceSelection: dependencyMap.ShowDeviceSelection === true,
                    AllowMultipleDeviceSelection: dependencyMap.AllowMultipleDeviceSelection === true,
                    AllowedDependencyMapIds: dependencyMap.AllowedDependencyMapIds || [],
                    IntegrateWithIpam: dependencyMap.IntegrateWithIpam,
                    Image: dependencyMap.Image,
                    CollectionEntry: collectionEntry
                })
            }
            return resources;
        })();
        return from(promise);
    }

    getDependencyMapCommandForSelect(id: string): Observable<any> {
        let promise = (async ()=>{
           
            await this.syncService.checkAndSyncData('DependencyMap', 'DependencyMap');
            await this.syncService.checkAndSyncData('CollectionEntry', 'CollectionEntry');
           
            let dependencyMaps = await this.syncService.sync('DependencyMap');
            let allCollectionEntries = await this.syncService.sync('CollectionEntry');
            let resources: any[] = [];
            var commands = allCollectionEntries.filter(m => m.CollectionId == "698f82d8193046724890911d"); // Commands
            let dependencyMap = await this.getDependencyMapForSelect(id, dependencyMaps);
            if(dependencyMap && dependencyMap.CommandCollectionEntryIds && dependencyMap.CommandCollectionEntryIds.length > 0) {
                allCollectionEntries = await this.hydrateMissingCollectionEntries(dependencyMap.CommandCollectionEntryIds, allCollectionEntries);
                commands = allCollectionEntries.filter(m => m.CollectionId == "698f82d8193046724890911d");
                dependencyMap.CommandCollectionEntryIds.forEach(collectionEntryId => {
                    let collectionEntry = commands.find(collectionEntry =>
                        collectionEntry.Id == collectionEntryId
                    );
                    if(collectionEntry) {
                        resources.push({
                            CollectionEntryId: collectionEntry.Id,
                            CollectionId: collectionEntry.CollectionId,
                            CollectionName: collectionEntry.CollectionAttributeValues?.find(x=> x.Name.toLowerCase() == 'name')?.Value,
                            Description: collectionEntry.CollectionAttributeValues?.find(x=> x.Name.toLowerCase() == "description")?.Value,
                            Command: collectionEntry.CollectionAttributeValues?.find(x=> x.Name.toLowerCase() == "cli_command")?.Value,
                            Category: collectionEntry.CollectionAttributeValues?.find(x=> x.Name.toLowerCase() == "category")?.Value?.trim() || 'Others',
                            DependencyMapId: dependencyMap.Id,
                            CollectionEntry: collectionEntry
                        })
                    }
                })
            }
            return resources;
        })();
        return from(promise);
    }

    private async getDependencyMapForSelect(id: string, dependencyMaps: any[]): Promise<any> {
        // checkAndSyncData has already refreshed IndexedDB when needed. Prefer that
        // local record and only call GetById when the requested map is genuinely absent.
        const cachedDependencyMap = dependencyMaps.find(x => x.Id == id);
        if (cachedDependencyMap) {
            return cachedDependencyMap;
        }

        try {
            let dependencyMap = await this.getDependencyMapById(id).toPromise();
            if (dependencyMap) {
                await this.syncService.addDataInIndexDB([dependencyMap], 'DependencyMap');
                return dependencyMap;
            }
        } catch (error) {
        }
        return null;
    }

    private async hydrateMissingCollectionEntries(collectionEntryIds: string[], collectionEntries: any[]): Promise<any[]> {
        const uniqueIds = [...new Set(collectionEntryIds || [])].filter((collectionEntryId) => !!collectionEntryId);
        const existingIds = new Set((collectionEntries || []).map((collectionEntry) => collectionEntry.Id));
        const missingIds = uniqueIds.filter((collectionEntryId) => existingIds.has(collectionEntryId) === false);

        if (!missingIds.length) {
            return collectionEntries;
        }

        const missingCollectionEntries = [];
        const batchSize = 250;
        for (let index = 0; index < missingIds.length; index += batchSize) {
            const batch = missingIds.slice(index, index + batchSize);
            try {
                const batchCollectionEntries = await this.http
                    .post<any[]>(this.baseUrl + 'CollectionEntry/GetByIds', batch)
                    .toPromise();
                missingCollectionEntries.push(...(batchCollectionEntries || []));
            } catch (error) {
            }
        }

        if (missingCollectionEntries.length) {
            await this.syncService.addDataInIndexDB(missingCollectionEntries, 'CollectionEntry');
            collectionEntries = collectionEntries.concat(missingCollectionEntries);
        }

        return collectionEntries;
    }

    getAllCommandForSelect(): Observable<any> {
        let promise = (async ()=>{
           
            let allCollectionEntries = await this.syncService.sync('CollectionEntry') || [];
            let resources: any[] = [];
            var commands = allCollectionEntries.filter(m => m.CollectionId == "698f82d8193046724890911d");
            for(let i = 0; i < commands.length; i++) {
                let collectionEntry = commands[i];
                if(collectionEntry) {
                    resources.push({
                        CollectionEntryId: collectionEntry.Id,
                        CollectionId: collectionEntry.CollectionId,
                        CollectionName: collectionEntry.CollectionAttributeValues?.find(x=> x.Name.toLowerCase() == 'name')?.Value,
                        Description: collectionEntry.CollectionAttributeValues?.find(x=> x.Name.toLowerCase() == "description")?.Value,
                        Command: collectionEntry.CollectionAttributeValues?.find(x=> x.Name.toLowerCase() == "cli_command")?.Value,
                        Category: collectionEntry.CollectionAttributeValues?.find(x=> x.Name.toLowerCase() == "category")?.Value?.trim() || 'Others',
                        CollectionEntry: collectionEntry
                    })
                }
            }
            
            return resources;
        })();
        return from(promise);
    }

    getResourcesByDependencyMap(dependencyMapIds: string[], type: string = 'id') {
        let promise = (async ()=>{
            
            await this.syncService.checkAndSyncData('DependencyMap', 'DependencyMap');
            await this.syncService.checkAndSyncData('CollectionEntry', 'CollectionEntry');
            
            let dependencyMaps = await this.syncService.sync('DependencyMap');
            let allCollectionEntries = await this.syncService.sync('CollectionEntry');
            let resources: any[] = [];
            if(type == 'name') {
                dependencyMapIds.forEach((dependencyId, index) => {
                    let dependency = dependencyMaps.find(x=> x.Name?.trim()?.toLowerCase() == dependencyId?.trim()?.toLowerCase());
                    if(dependency) {
                        dependencyMapIds[index] = dependency.Id;
                    }
                })
            }
            for(let i = 0; i < dependencyMapIds.length; i++) {
                let id = dependencyMapIds[i];
                let dependencyMap = await this.getDependencyMapForSelect(id, dependencyMaps);
                let collectionEntryIds = this.getCollectionEntryIds(dependencyMap.CollectionDependencies);
                allCollectionEntries = await this.hydrateMissingCollectionEntries(collectionEntryIds, allCollectionEntries);
                let collectionEntries = allCollectionEntries.filter(x => 
                    collectionEntryIds.includes(x.Id)
                );
                for(let j = 0; j < collectionEntries.length; j++) {
                    let collectionEntry= collectionEntries[j];
                    resources.push({
                        CollectionEntryId: collectionEntry.Id,
                        CollectionId: collectionEntry.CollectionId,
                        CollectionName: collectionEntry.CollectionAttributeValues.find(x=> x.Name == 'Name')?.Value,
                        DependencyMapId: id,
                        Description: collectionEntry.CollectionAttributeValues.find(x=> x.Name == 'Description')?.Value,
                        ImagePath: collectionEntry.CollectionAttributeValues.find(x=> x.Name == 'Image')?.Value,
                        TerraformResourceName: collectionEntry.CollectionAttributeValues.find(x=> x.Name == 'Terraform Resource Name')?.Value
                    });
                }
            }
            return resources;
        })();
        return from(promise)
    }

    getResourcesByCollectionEntryIds(collectionEntryIds: string[]): Observable<any[]> {
        let promise = (async () => {
            const uniqueIds = [...new Set((collectionEntryIds || []).filter(id => !!id))];
            if (!uniqueIds.length) {
                return [];
            }

            await this.syncService.checkAndSyncData('CollectionEntry', 'CollectionEntry');

            let allCollectionEntries = await this.syncService.sync('CollectionEntry');
            allCollectionEntries = await this.hydrateMissingCollectionEntries(uniqueIds, allCollectionEntries);
            const collectionEntries = allCollectionEntries.filter(collectionEntry => uniqueIds.includes(collectionEntry.Id));

            return collectionEntries.map(collectionEntry => ({
                CollectionEntryId: collectionEntry.Id,
                CollectionId: collectionEntry.CollectionId,
                CollectionName: collectionEntry.CollectionAttributeValues?.find(x => x.Name == 'Name')?.Value,
                Description: collectionEntry.CollectionAttributeValues?.find(x => x.Name == 'Description')?.Value,
                ImagePath: collectionEntry.CollectionAttributeValues?.find(x => x.Name == 'Image')?.Value
            }));
        })();

        return from(promise);
    }

    getDependencyMapWithCollectionEntry(id: string, isRefresh: boolean = false) {
        let promise = (async () => {
           
            await this.syncService.checkAndSyncData('DependencyMap', 'DependencyMap')
            await this.syncService.checkAndSyncData('CollectionEntry', 'CollectionEntry')
            
            let dependencyMaps = await this.syncService.sync('DependencyMap');
            let allCollectionEntries = await this.syncService.sync('CollectionEntry');
            let dependencyMap = await this.getDependencyMapForSelect(id, dependencyMaps);
            let collectionEntryIds = this.getCollectionEntryIds(dependencyMap.CollectionDependencies);
            allCollectionEntries = await this.hydrateMissingCollectionEntries(collectionEntryIds, allCollectionEntries);
            let collectionEntries = allCollectionEntries.filter(x => 
                collectionEntryIds.includes(x.Id)
            );
            return { dependencyMap: dependencyMap, collectionEntries: collectionEntries};
        })();

        return from(promise);
    }

    getCollectionEntryIds(dependencyMapCollectionDependencies) {
        const result = [];

        for (const dependency of dependencyMapCollectionDependencies) {
            if (dependency.CollectionEntryId && dependency.CollectionEntryId.trim() !== "") {
            result.push(dependency.CollectionEntryId);
            }

            if (dependency.ChildCollections && dependency.ChildCollections.length > 0) {
            result.push(...this.getCollectionEntryIds(dependency.ChildCollections));
            }
        }

        return result;
    }

    getUnusedResourcesByDependencyMap(
        dependencyMapId: string[]
    ): Observable<CatalogFiltersFeatures[]> {
        return this.http.post<CatalogFiltersFeatures[]>(
            this.baseUrl + this.baseEndpoint + '/GetUnusedResourcesByDependencyMap',
            dependencyMapId
        );
    }

    postDependency(dependencyMap: DependencyMap): Observable<DependencyMap> {
        this.syncService.lastSyncTime['DependencyMapUpdateBySelf'] = true;
        return this.http.post<DependencyMap>(this.baseUrl + this.baseEndpoint + '', dependencyMap);
    }

    editDependency(id: string, dependencyMap: DependencyMap): Observable<DependencyMap> {
        this.syncService.lastSyncTime['DependencyMapUpdateBySelf'] = true;
        return this.http.put<DependencyMap>(
            this.baseUrl + this.baseEndpoint + '/' + id + '?id=' + id,
            dependencyMap
        );
    }

    deleteDependency(dependencyMapId: string): Observable<DependencyMap> {
        this.syncService.lastSyncTime['DependencyMapUpdateBySelf'] = true;
        return this.http.delete<DependencyMap>(
            this.baseUrl + this.baseEndpoint + '/' + dependencyMapId
        );
    }

    deleteDependencies(dependencyMaps: any[]): Observable<any[]> {
        this.syncService.lastSyncTime['DependencyMapUpdateBySelf'] = true;
        return this.http.post<any[]>(
            this.baseUrl + this.baseEndpoint + '/DeleteDependencies',
            dependencyMaps
        );
    }

    getDependencyMapById(id: string): Observable<DependencyMap> {
        return this.http.get<DependencyMap>(this.baseUrl + this.baseEndpoint + '/GetById/' + id);
    }

    getResourcesByDependencyMapByIdPaginated(filter: any = {}) {
        let promise = (async ()=>{
            if(filter.Cloud) {
                await this.syncService.checkAndSyncData('DependencyMap', 'DependencyMap');
                await this.syncService.checkAndSyncData('CollectionEntry', 'CollectionEntry');
                
                let dependencyMaps = await this.syncService.sync('DependencyMap');
                let allCollectionEntries = await this.syncService.sync('CollectionEntry');
                let resources: any[] = [];
                let dependencyMap = await this.getDependencyMapForSelect(filter.Cloud, dependencyMaps);
                let collectionEntryIds = this.getCollectionEntryIds(dependencyMap.CollectionDependencies);
                allCollectionEntries = await this.hydrateMissingCollectionEntries(collectionEntryIds, allCollectionEntries);
                let collectionEntries = allCollectionEntries.filter(x => 
                    collectionEntryIds.includes(x.Id)
                );

                let filteredEntries = collectionEntries;
                if (filter.Filter && filter.Filter.trim() !== "") {
                    let lowerSearch = filter.Filter.toLowerCase();
                    filteredEntries = collectionEntries.filter(entry => {
                        let name = entry.CollectionAttributeValues.find(x => x.Name === "Name")?.Value?.toLowerCase() || "";
                        return name.includes(lowerSearch);
                    });
                }
                let startIndex = filter.PageNumber * filter.PageSize;
                let endIndex = startIndex + filter.PageSize;
                let pagedEntries = filteredEntries.slice(startIndex, endIndex);
                for(let j = 0; j < pagedEntries.length; j++) {
                    let collectionEntry= pagedEntries[j];
                    resources.push({
                        CollectionEntryId: collectionEntry.Id,
                        CollectionId: collectionEntry.CollectionId,
                        CollectionName: collectionEntry.CollectionAttributeValues.find(x=> x.Name == 'Name')?.Value,
                        DependencyMapId: dependencyMap.Id,
                        Description: collectionEntry.CollectionAttributeValues.find(x=> x.Name == 'Description')?.Value,
                        ImagePath: collectionEntry.CollectionAttributeValues.find(x=> x.Name == 'Image')?.Value
                    });
                }
                
                return {
                    items: resources,
                    totalCount: filteredEntries.length
                };
            } else {
                return {
                    items: [],
                    totalCount: 0
                };
            }
        })();
        return from(promise)
    }
}
